import { RedoItemFulfillment } from "@redotech/redo-fulfillment-common/redo-item-fulfillment";
import { returnsCoverageIsEnabled } from "@redotech/redo-return-coverage-common/coverage-enabled";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { changedTeamPatchPaths } from "../grouped-settings-changes";
import {
  PackageProtectionPricingChangesSchema,
  packageProtectionPricingFromSettings,
  PackageProtectionPricingSchema,
  type PackageProtectionPricingSource,
  packageProtectionPricingTeamPatch,
} from "../package-protection/package-protection-pricing";
import {
  diffSettingsGroups,
  mergeSettingsGroups,
  type SettingsGroups,
} from "../tab-settings/settings-group-diff";

export const ReturnsCoverageSettingsSchema = z.object({
  returnCoverage: z.object({
    coveredMethods: z.object({
      storeCredit: z.boolean(),
      exchange: z.boolean(),
      refund: z.boolean(),
    }),
    excludeProductTags: z.array(z.string()),
    excludeCollections: z.array(z.string()),
    excludeCustomerTags: z.array(z.string()),
    excludeProperties: z.array(z.string()),
    excludeVariantMetafields: z.array(z.string()),
    excludeOrderTags: z.array(z.string()),
    excludeSources: z.array(z.string()),
    excludeDiscountCodes: z.array(z.string()),
    requireExactDiscountMatch: z.boolean(),
    excludeInStorePickup: z.boolean(),
    excludeSkus: z.array(z.string()),
  }),
  commentSold: z.object({
    finalSaleReturnsEnabled: z.boolean(),
    excludeCentEndings: z.array(z.string()),
    discountsAreFinalSales: z.boolean(),
    excludeLocalPickup: z.boolean(),
    couponedItemsDiscountThreshold: z.number().nullable(),
  }),
  packageProtection: PackageProtectionPricingSchema,
  display: z.object({
    showOnProductPage: z.boolean(),
    checkboxTextColor: z.string(),
    checkboxText: z.string(),
    checkboxFontSize: z.number(),
    checkboxSelector: z.string(),
  }),
  advancedProtection: z.object({
    redoItemFulfillment: createEnum(RedoItemFulfillment),
    preProtectedOrdersEnabled: z.boolean(),
    deleteCartProperties: z.boolean(),
  }),
});

export type ReturnsCoverageSettings = z.infer<
  typeof ReturnsCoverageSettingsSchema
>;

/**
 * Pricing changes are bounded here because `updateOne` skips the Mongoose
 * validators. Under Package Protection Plus the pricing paths are admin-only,
 * which the endpoint enforces with the shared admin-only path check.
 */
export const ReturnsCoverageSettingsChangesSchema = z.object({
  returnCoverage: ReturnsCoverageSettingsSchema.shape.returnCoverage
    .partial()
    .optional(),
  commentSold: ReturnsCoverageSettingsSchema.shape.commentSold
    .partial()
    .optional(),
  display: ReturnsCoverageSettingsSchema.shape.display.partial().optional(),
  advancedProtection: ReturnsCoverageSettingsSchema.shape.advancedProtection
    .partial()
    .optional(),
  packageProtection: PackageProtectionPricingChangesSchema.optional(),
});

export type ReturnsCoverageSettingsChanges = z.infer<
  typeof ReturnsCoverageSettingsChangesSchema
>;

export interface ReturnsCoverageTeamPatch {
  /** Dotted paths relative to `team.settings`. */
  set: Record<string, unknown>;
  unset: string[];
  /** `team.automation.fulfill_redo_item`, which lives outside settings. */
  redoItemFulfillment?: RedoItemFulfillment;
  /** `team.portal.injection_text_color`, which lives outside settings. */
  checkboxTextColor?: string;
}

const DEFAULT_CHECKBOX_FONT_SIZE = 12;

const SETTINGS_GROUP_KEYS = [
  "returnCoverage",
  "commentSold",
  "display",
  "advancedProtection",
  "packageProtection",
];

/** The slice of team settings the Coverage tab reads. */
export interface ReturnsCoverageSettingsSource {
  automation?: { fulfill_redo_item?: RedoItemFulfillment | null } | null;
  portal?: { injection_text_color?: string | null } | null;
  settings: {
    countriesPackageProtection: string[];
    coverage?: { storeCredit: boolean; exchange: boolean; refund: boolean };
    coverageExcludeProductTags: string[];
    coverageExcludeCollections: string[];
    coverageExcludeCustomerTags: string[];
    coverageExcludeProperties: string[];
    coverageExcludeTags?: string[];
    coverageExcludeSources?: string[];
    coverageExcludeDiscountCodes?: string[];
    coverageExcludeCentEnding?: string[];
    discountCodePartialMatch?: boolean | null;
    shopifyExtension?: {
      coverageExcludePickup?: boolean | null;
      deleteCartProperties?: boolean | null;
    } | null;
    preProtectedOrdersEnabled?: boolean | null;
    hideCheckbox?: boolean | null;
    html?: string | null;
    checkboxFontSize?: number | null;
    checkboxSelector?: string | null;
    publicapi?: {
      discountsAreFinalSales?: boolean | null;
      coverageExcludeLocalPickup?: boolean | null;
      couponedItemsDiscountThreshold?: number | null;
    } | null;
    packageProtection?: PackageProtectionPricingSource | null;
    returns?: {
      enabled?: boolean | null;
      coverageExcludedVariantMetafields?: string[] | null;
      returnsCoverageExcludedSkus?: string[] | null;
      finalSaleReturns?: { enabled?: boolean | null } | null;
    } | null;
  };
}

function settingsGroups(settings: ReturnsCoverageSettings): SettingsGroups {
  return {
    returnCoverage: settings.returnCoverage,
    commentSold: settings.commentSold,
    display: settings.display,
    advancedProtection: settings.advancedProtection,
    packageProtection: settings.packageProtection,
  };
}

export function returnsCoverageSettingsFromTeam({
  settings,
  automation,
  portal,
}: ReturnsCoverageSettingsSource): ReturnsCoverageSettings {
  return {
    returnCoverage: {
      coveredMethods: {
        storeCredit: settings.coverage?.storeCredit ?? false,
        exchange: settings.coverage?.exchange ?? false,
        refund: settings.coverage?.refund ?? false,
      },
      excludeProductTags: settings.coverageExcludeProductTags,
      excludeCollections: settings.coverageExcludeCollections,
      excludeCustomerTags: settings.coverageExcludeCustomerTags,
      excludeProperties: settings.coverageExcludeProperties,
      excludeVariantMetafields:
        settings.returns?.coverageExcludedVariantMetafields ?? [],
      excludeOrderTags: settings.coverageExcludeTags ?? [],
      excludeSources: settings.coverageExcludeSources ?? [],
      excludeDiscountCodes: settings.coverageExcludeDiscountCodes ?? [],
      requireExactDiscountMatch: !settings.discountCodePartialMatch,
      excludeInStorePickup:
        settings.shopifyExtension?.coverageExcludePickup ?? false,
      excludeSkus: settings.returns?.returnsCoverageExcludedSkus ?? [],
    },
    commentSold: {
      finalSaleReturnsEnabled:
        settings.returns?.finalSaleReturns?.enabled ?? false,
      excludeCentEndings: settings.coverageExcludeCentEnding ?? [],
      discountsAreFinalSales:
        settings.publicapi?.discountsAreFinalSales ?? false,
      excludeLocalPickup:
        settings.publicapi?.coverageExcludeLocalPickup ?? false,
      couponedItemsDiscountThreshold:
        settings.publicapi?.couponedItemsDiscountThreshold || null,
    },
    packageProtection: packageProtectionPricingFromSettings({
      packageProtection: settings.packageProtection,
      countriesPackageProtection: settings.countriesPackageProtection,
    }),
    display: {
      showOnProductPage: !settings.hideCheckbox,
      checkboxTextColor: portal?.injection_text_color ?? "",
      checkboxText: settings.html ?? "",
      checkboxFontSize: settings.checkboxFontSize || DEFAULT_CHECKBOX_FONT_SIZE,
      checkboxSelector: settings.checkboxSelector ?? "",
    },
    advancedProtection: {
      redoItemFulfillment:
        automation?.fulfill_redo_item ?? RedoItemFulfillment.PARTIAL,
      preProtectedOrdersEnabled: settings.preProtectedOrdersEnabled ?? false,
      deleteCartProperties:
        settings.shopifyExtension?.deleteCartProperties ?? true,
    },
  };
}

export function returnsCoverageSettingsChanges(
  initial: ReturnsCoverageSettings,
  next: ReturnsCoverageSettings,
): ReturnsCoverageSettingsChanges {
  return ReturnsCoverageSettingsChangesSchema.parse(
    diffSettingsGroups(
      SETTINGS_GROUP_KEYS,
      settingsGroups(initial),
      settingsGroups(next),
    ),
  );
}

function applyChanges(
  base: ReturnsCoverageSettings,
  changes: ReturnsCoverageSettingsChanges,
): ReturnsCoverageSettings {
  return ReturnsCoverageSettingsSchema.parse(
    mergeSettingsGroups(SETTINGS_GROUP_KEYS, settingsGroups(base), changes),
  );
}

export function returnsCoverageSettingsToTeamPatch(
  next: ReturnsCoverageSettings,
  { hasFinalSaleReturns }: { hasFinalSaleReturns: boolean },
): ReturnsCoverageTeamPatch {
  const { returnCoverage, commentSold } = next;
  const set: Record<string, unknown> = {
    coverage: returnCoverage.coveredMethods,
    coverageExcludeProductTags: returnCoverage.excludeProductTags,
    coverageExcludeCollections: returnCoverage.excludeCollections,
    coverageExcludeCustomerTags: returnCoverage.excludeCustomerTags,
    coverageExcludeProperties: returnCoverage.excludeProperties,
    "returns.coverageExcludedVariantMetafields":
      returnCoverage.excludeVariantMetafields,
    coverageExcludeTags: returnCoverage.excludeOrderTags,
    coverageExcludeSources: returnCoverage.excludeSources,
    coverageExcludeDiscountCodes: returnCoverage.excludeDiscountCodes,
    discountCodePartialMatch: !returnCoverage.requireExactDiscountMatch,
    "shopifyExtension.coverageExcludePickup":
      returnCoverage.excludeInStorePickup,
    "returns.returnsCoverageExcludedSkus": returnCoverage.excludeSkus,
    coverageExcludeCentEnding: commentSold.excludeCentEndings,
    "publicapi.discountsAreFinalSales": commentSold.discountsAreFinalSales,
    "publicapi.coverageExcludeLocalPickup": commentSold.excludeLocalPickup,
    preProtectedOrdersEnabled:
      next.advancedProtection.preProtectedOrdersEnabled,
    "shopifyExtension.deleteCartProperties":
      next.advancedProtection.deleteCartProperties,
    hideCheckbox: !next.display.showOnProductPage,
    html: next.display.checkboxText,
    checkboxFontSize: next.display.checkboxFontSize,
    checkboxSelector: next.display.checkboxSelector,
  };
  const pricing = packageProtectionPricingTeamPatch(next.packageProtection);
  Object.assign(set, pricing.set);
  const unset: string[] = [...pricing.unset];
  // Legacy only flipped the flag on a team that already had the block.
  if (hasFinalSaleReturns) {
    set["returns.finalSaleReturns.enabled"] =
      commentSold.finalSaleReturnsEnabled;
  }
  if (commentSold.couponedItemsDiscountThreshold === null) {
    unset.push("publicapi.couponedItemsDiscountThreshold");
  } else {
    set["publicapi.couponedItemsDiscountThreshold"] =
      commentSold.couponedItemsDiscountThreshold;
  }

  return { set, unset };
}

export function returnsCoverageTeamPatchForChanges(
  current: ReturnsCoverageSettingsSource,
  changes: ReturnsCoverageSettingsChanges,
): ReturnsCoverageTeamPatch {
  const stored = returnsCoverageSettingsFromTeam(current);
  const options = {
    hasFinalSaleReturns: !!current.settings.returns?.finalSaleReturns,
  };
  const next = applyChanges(stored, changes);
  const patch = changedTeamPatchPaths(
    returnsCoverageSettingsToTeamPatch(stored, options),
    returnsCoverageSettingsToTeamPatch(next, options),
  );
  const result: ReturnsCoverageTeamPatch = { ...patch };
  const fulfillment = next.advancedProtection.redoItemFulfillment;
  if (fulfillment !== stored.advancedProtection.redoItemFulfillment) {
    result.redoItemFulfillment = fulfillment;
  }
  const textColor = next.display.checkboxTextColor;
  if (textColor !== stored.display.checkboxTextColor) {
    result.checkboxTextColor = textColor;
  }
  if (!("coverage" in result.set)) {
    return result;
  }
  const { coveredMethods } = next.returnCoverage;
  result.set.coverageEnabled = returnsCoverageIsEnabled(
    {
      exchange: coveredMethods.exchange,
      refund: coveredMethods.refund,
      credit: coveredMethods.storeCredit,
    },
    current.settings.returns?.enabled ?? false,
  );
  return result;
}
